import type { ComponentDefinition } from "./types";
import {
    backgroundClassOptions,
    blockAlignClassOptions,
    borderColorClassOptions,
    borderWidthClassOptions,
    buttonColorClassOptions,
    fontSizeClassOptions,
    fontWeightClassOptions,
    gutterClassOptions,
    innerGapClassOptions,
    marginXClassOptions,
    marginYClassOptions,
    paddingAllClassOptions,
    roundedClassOptions,
    sectionContentAlignClassOptions,
    sectionContentJustifyClassOptions,
    shadowClassOptions,
    textAlignClassOptions,
} from "./options";

const sectionBodyContentAccepts = [
    "HeadingBlock",
    "QuillText",
    "ImageBlock",
    "ButtonLink",
    "DocumentLink",
    "NewsCard",
    "EventCard",
] as const;

const multiColumnAndGridContentAccepts = [
    "HeadingBlock",
    "QuillText",
    "ImageBlock",
    "ButtonLink",
    "Card",
    "DocumentLink",
    "NewsCard",
    "EventCard",
] as const;
const CARD_IMAGE_CONTEXT_PARAM = "__cardImageContext";

function applyRosterAccordionEditing(values: any, editingInfo: any) {
    const isAccordion = values?.viewMode === "accordion";
    const fields = (editingInfo?.fields ?? []).map((field: any) => {
        if (!isAccordion && field?.path === "accordionStartOpen") {
            return { ...field, visible: false };
        }
        return field;
    });

    return {
        ...(editingInfo ?? {}),
        fields,
    };
}

function hideEditingFields(editingInfo: any, hiddenPaths: string[]) {
    const hiddenPathSet = new Set(hiddenPaths);
    const fields = (editingInfo?.fields ?? []).map((field: any) => {
        if (hiddenPathSet.has(field?.path ?? "")) {
            return { ...field, visible: false };
        }
        return field;
    });

    return {
        ...(editingInfo ?? {}),
        fields,
    };
}

// Component definitions (schemas)
export const componentDefinitions: ComponentDefinition[] = [
    // ========== LAYOUT ==========
    {
        id: "RootLayoutSections",
        label: "Root Layout Sections",
        schema: [
            {
                prop: "Sections",
                type: "component-collection",
                accepts: [
                    "HeroImageSection",
                    "SingleColumn",
                    "ColumnsSection",
                    "GridSection",
                    "CardsSection",
                    "CTASection",
                    "AccordionSection",
                    "VideoEmbedSection",
                    "NewsLatestRow",
                    "EventsRow",
                    "PhotoAlbum",
                    "RosterAccordion",
                    "NeighborhoodsAccordion",
                    "ResidentDirectorySection",
                    "DocumentsAccordion",
                    "ListTemplateSection",
                ],
                placeholderAppearance: { label: "Add Section", height: 48 },
            },
        ],
    },

    {
        id: "QuillText",
        label: "Rich Text",
        thumbnail: "https://friscolakes.net/easyblocks-web-builder/build/thumbnails/rich-text.svg",
        schema: [
            {
                prop: "delta",
                type: "string",
                defaultValue: "",
            },
            {
                prop: "paddingClass",
                type: "select",
                label: "Padding (Vertical)",
                params: {
                    options: [
                        { value: "py-0", label: "None" },
                        { value: "py-section-sm", label: "Small" },
                        { value: "py-section-md", label: "Medium" },
                        { value: "py-section-lg", label: "Large" },
                    ],
                },
                defaultValue: "py-0",
            },
        ],
        editing: ({ editingInfo }: any) => {
            const fields = (editingInfo?.fields ?? []).map((field: any) =>
                field?.path === "delta" ? { ...field, visible: false } : field
            );
            return {
                ...(editingInfo ?? {}),
                fields,
            };
        },
    },
    {
        id: "HeadingBlock",
        label: "Heading",
        thumbnail: "https://friscolakes.net/easyblocks-web-builder/build/thumbnails/heading-block.svg",
        schema: [
            {
                prop: "text",
                type: "string",
                label: "Text",
                defaultValue: "Heading Text",
            },
            {
                prop: "level",
                type: "select",
                label: "Level",
                params: {
                    options: [
                        { value: "h1", label: "H1" },
                        { value: "h2", label: "H2" },
                        { value: "h3", label: "H3" },
                        { value: "h4", label: "H4" },
                        { value: "h5", label: "H5" },
                        { value: "h6", label: "H6" },
                    ],
                },
                defaultValue: "h2",
            },
            {
                prop: "paddingClass",
                type: "select",
                label: "Padding (Vertical)",
                params: {
                    options: [
                        { value: "py-0", label: "None" },
                        { value: "py-section-sm", label: "Small" },
                        { value: "py-section-md", label: "Medium" },
                        { value: "py-section-lg", label: "Large" },
                    ],
                },
                defaultValue: "py-0",
            },
            {
                prop: "alignClass",
                type: "select",
                label: "Content Horizontal Alignment",
                params: { options: textAlignClassOptions },
                defaultValue: "no-class",
            },
            {
                prop: "textClass",
                type: "textColor",
                label: "Color",
                defaultValue: "no-class",
            },
            {
                prop: "fsClass",
                type: "select",
                label: "Font size",
                params: { options: fontSizeClassOptions },
                defaultValue: "no-class",
            },
            {
                prop: "fwClass",
                type: "select",
                label: "Font weight",
                params: { options: fontWeightClassOptions },
                defaultValue: "no-class",
            },
        ],
    },
    {
        id: "NewsCard",
        label: "Single News Item (external)",
        thumbnail: "https://friscolakes.net/easyblocks-web-builder/build/thumbnails/news-card.svg",
        schema: [
            { prop: "newsItem", label: "News item", type: "news", defaultValue: null, optional: true },
            {
                prop: "paddingClass",
                type: "select",
                label: "Padding (Vertical)",
                params: {
                    options: [
                        { value: "py-0", label: "None" },
                        { value: "py-section-sm", label: "Small" },
                        { value: "py-section-md", label: "Medium" },
                        { value: "py-section-lg", label: "Large" },
                    ],
                },
                defaultValue: "py-0",
            },
            {
                prop: "cardBgClass",
                type: "backgroundColor",
                label: "Card Background",
                defaultValue: "text-bg-white",
            },
            {
                prop: "cardBorderClass",
                type: "select",
                label: "Card Border Width",
                params: { options: borderWidthClassOptions },
                defaultValue: "no-class",
            },
            {
                prop: "cardBorderColorClass",
                type: "borderColor",
                label: "Card Border Color",
                defaultValue: "no-class",
            },
            {
                prop: "contentAlignClass",
                type: "select",
                label: "Content Horizontal Alignment",
                params: { options: textAlignClassOptions },
                defaultValue: "no-class",
            },
            {
                prop: "buttonColorClass",
                type: "buttonColor",
                label: "Button Color",
                defaultValue: "primary",
            },
        ],
    },
    {
        id: "NewsLatestRow",
        label: "Latest News (Auto)",
        thumbnail: "https://friscolakes.net/easyblocks-web-builder/build/thumbnails/news-latest-row.svg",
        schema: [
            {
                prop: "bgClass",
                type: "backgroundColor",
                label: "Background Color",
                defaultValue: "text-bg-white",
            },
            {
                prop: "paddingYClass",
                type: "select",
                label: "Padding (Vertical)",
                params: {
                    options: [
                        { value: "py-0", label: "None" },
                        { value: "py-section-sm", label: "Small" },
                        { value: "py-section-md", label: "Medium" },
                        { value: "py-section-lg", label: "Large" },
                    ],
                },
                defaultValue: "py-section-md",
            },
            {
                prop: "viewMode",
                type: "select",
                label: "View Mode",
                params: {
                    options: [
                        { value: "cards", label: "Cards" },
                        { value: "table", label: "Table" },
                    ],
                },
                defaultValue: "cards",
            },
            {
                prop: "itemLimit",
                type: "select",
                label: "Number of Items",
                params: {
                    options: [
                        { value: "3", label: "3" },
                        { value: "6", label: "6" },
                        { value: "all", label: "All" },
                    ],
                },
                defaultValue: "3",
            },
            {
                prop: "cardBgClass",
                type: "backgroundColor",
                label: "Card Background",
                defaultValue: "text-bg-white",
            },
            {
                prop: "cardBorderClass",
                type: "select",
                label: "Card Border Width",
                params: { options: borderWidthClassOptions },
                defaultValue: "no-class",
            },
            {
                prop: "cardBorderColorClass",
                type: "borderColor",
                label: "Card Border Color",
                defaultValue: "no-class",
            },
            {
                prop: "contentAlignClass",
                type: "select",
                label: "Content Horizontal Alignment",
                params: { options: textAlignClassOptions },
                defaultValue: "no-class",
            },
            {
                prop: "buttonColorClass",
                type: "buttonColor",
                label: "Button Color",
                defaultValue: "primary",
            },
        ],
        editing: ({ values, editingInfo }: any) => {
            const isCards = values?.viewMode === "cards";
            const fields = (editingInfo?.fields ?? []).map((field: any) => {
                // Hide card options when table mode is selected
                if (
                    !isCards &&
                    (field?.path === "cardBgClass" ||
                        field?.path === "cardBorderClass" ||
                        field?.path === "cardBorderColorClass" ||
                        field?.path === "contentAlignClass")
                ) {
                    return { ...field, visible: false };
                }
                return field;
            });
            return {
                ...(editingInfo ?? {}),
                fields,
            };
        },
    },
    {
        id: "EventCard",
        label: "Single Event Item (external)",
        thumbnail: "https://friscolakes.net/easyblocks-web-builder/build/thumbnails/event-card.svg",
        schema: [
            { prop: "eventItem", label: "Event item", type: "events", defaultValue: null, optional: true },
            {
                prop: "paddingClass",
                type: "select",
                label: "Padding (Vertical)",
                params: {
                    options: [
                        { value: "py-0", label: "None" },
                        { value: "py-section-sm", label: "Small" },
                        { value: "py-section-md", label: "Medium" },
                        { value: "py-section-lg", label: "Large" },
                    ],
                },
                defaultValue: "py-0",
            },
            {
                prop: "cardBgClass",
                type: "backgroundColor",
                label: "Card Background",
                defaultValue: "text-bg-white",
            },
            {
                prop: "cardBorderClass",
                type: "select",
                label: "Card Border Width",
                params: { options: borderWidthClassOptions },
                defaultValue: "no-class",
            },
            {
                prop: "cardBorderColorClass",
                type: "borderColor",
                label: "Card Border Color",
                defaultValue: "no-class",
            },
            {
                prop: "contentAlignClass",
                type: "select",
                label: "Content Horizontal Alignment",
                params: { options: textAlignClassOptions },
                defaultValue: "no-class",
            },
            {
                prop: "buttonColorClass",
                type: "buttonColor",
                label: "View Button Color",
                defaultValue: "primary",
            },
            {
                prop: "calendarButtonColorClass",
                type: "buttonColor",
                label: "Calendar Button Color",
                defaultValue: "primary-100",
            },
        ],
    },
    {
        id: "EventsRow",
        label: "Upcoming Events (Auto)",
        thumbnail: "https://friscolakes.net/easyblocks-web-builder/build/thumbnails/events-row.svg",
        schema: [
            {
                prop: "bgClass",
                type: "backgroundColor",
                label: "Background Color",
                defaultValue: "text-bg-white",
            },
            {
                prop: "paddingYClass",
                type: "select",
                label: "Padding (Vertical)",
                params: {
                    options: [
                        { value: "py-0", label: "None" },
                        { value: "py-section-sm", label: "Small" },
                        { value: "py-section-md", label: "Medium" },
                        { value: "py-section-lg", label: "Large" },
                    ],
                },
                defaultValue: "py-section-md",
            },
            {
                prop: "viewMode",
                type: "select",
                label: "View Mode",
                params: {
                    options: [
                        { value: "cards", label: "Cards" },
                        { value: "table", label: "Table" },
                    ],
                },
                defaultValue: "cards",
            },
            {
                prop: "itemLimit",
                type: "select",
                label: "Number of Items",
                params: {
                    options: [
                        { value: "3", label: "3" },
                        { value: "6", label: "6" },
                        { value: "9", label: "9" },
                        { value: "all", label: "All" },
                    ],
                },
                defaultValue: "3",
            },
            {
                prop: "cardBgClass",
                type: "backgroundColor",
                label: "Card Background",
                defaultValue: "text-bg-white",
            },
            {
                prop: "cardBorderClass",
                type: "select",
                label: "Card Border Width",
                params: { options: borderWidthClassOptions },
                defaultValue: "no-class",
            },
            {
                prop: "cardBorderColorClass",
                type: "borderColor",
                label: "Card Border Color",
                defaultValue: "no-class",
            },
            {
                prop: "contentAlignClass",
                type: "select",
                label: "Content Horizontal Alignment",
                params: { options: textAlignClassOptions },
                defaultValue: "no-class",
            },
            {
                prop: "buttonColorClass",
                type: "buttonColor",
                label: "View Button Color",
                defaultValue: "primary",
            },
            {
                prop: "calendarButtonColorClass",
                type: "buttonColor",
                label: "Calendar Button Color",
                defaultValue: "primary-100",
            },
        ],
        editing: ({ values, editingInfo }: any) => {
            const isCards = values?.viewMode === "cards";
            const fields = (editingInfo?.fields ?? []).map((field: any) => {
                // Hide card options when table mode is selected
                if (
                    !isCards &&
                    (field?.path === "cardBgClass" ||
                        field?.path === "cardBorderClass" ||
                        field?.path === "cardBorderColorClass" ||
                        field?.path === "contentAlignClass")
                ) {
                    return { ...field, visible: false };
                }
                return field;
            });
            return {
                ...(editingInfo ?? {}),
                fields,
            };
        },
    },
    {
        id: "RosterAccordion",
        label: "Club Roster (Auto)",
        thumbnail: "https://friscolakes.net/easyblocks-web-builder/build/thumbnails/roster-accordion.svg",
        schema: [
            {
                prop: "title",
                type: "string",
                label: "Section Title",
                defaultValue: "Club Roster",
            },
            {
                prop: "bgClass",
                type: "backgroundColor",
                label: "Background Color",
                defaultValue: "text-bg-white",
            },
            {
                prop: "paddingYClass",
                type: "select",
                label: "Padding (Vertical)",
                params: {
                    options: [
                        { value: "py-0", label: "None" },
                        { value: "py-section-sm", label: "Small" },
                        { value: "py-section-md", label: "Medium" },
                        { value: "py-section-lg", label: "Large" },
                    ],
                },
                defaultValue: "py-section-md",
            },
            {
                prop: "showPhone",
                type: "boolean",
                label: "Show Phone",
                defaultValue: true,
            },
            {
                prop: "showEmail",
                type: "boolean",
                label: "Show Email",
                defaultValue: true,
            },
            {
                prop: "showRosterNotes",
                type: "boolean",
                label: "Show Notes",
                defaultValue: true,
            },
            {
                prop: "showIsMember",
                type: "boolean",
                label: "Show Member Column",
                defaultValue: true,
            },
            {
                prop: "showIsBoard",
                type: "boolean",
                label: "Show Board Column",
                defaultValue: true,
            },
            {
                prop: "showSegmentColumns",
                type: "boolean",
                label: "Show Segment Columns",
                defaultValue: true,
            },
            {
                prop: "rowFilter",
                type: "rosterRowFilter",
                label: "Row Filter",
                defaultValue: "all",
            },
            {
                prop: "viewMode",
                type: "select",
                label: "View Mode",
                params: {
                    options: [
                        { value: "table", label: "Table" },
                        { value: "accordion", label: "Collapsible" },
                    ],
                },
                defaultValue: "accordion",
            },
            {
                prop: "accordionStartOpen",
                type: "select",
                label: "Accordion Start State",
                params: {
                    options: [
                        { value: "true", label: "Open" },
                        { value: "false", label: "Closed" },
                    ],
                },
                defaultValue: "false",
            },
        ],
        editing: ({ values, editingInfo }: any) => {
            return applyRosterAccordionEditing(values, editingInfo);
        },
    },
    {
        id: "NeighborhoodsAccordion",
        label: "Neighborhood Resident List (Auto)",
        thumbnail: "https://friscolakes.net/easyblocks-web-builder/build/thumbnails/neighborhoods-accordion.svg",
        schema: [
            {
                prop: "title",
                type: "string",
                label: "Section Title",
                defaultValue: "Neighborhood Resident List",
            },
            {
                prop: "bgClass",
                type: "backgroundColor",
                label: "Background Color",
                defaultValue: "text-bg-white",
            },
            {
                prop: "paddingYClass",
                type: "select",
                label: "Padding (Vertical)",
                params: {
                    options: [
                        { value: "py-0", label: "None" },
                        { value: "py-section-sm", label: "Small" },
                        { value: "py-section-md", label: "Medium" },
                        { value: "py-section-lg", label: "Large" },
                    ],
                },
                defaultValue: "py-section-md",
            },
            {
                prop: "showPhone",
                type: "boolean",
                label: "Show Phone",
                defaultValue: true,
            },
            {
                prop: "showEmail",
                type: "boolean",
                label: "Show Email",
                defaultValue: true,
            },
            {
                prop: "showAddress",
                type: "boolean",
                label: "Show Address",
                defaultValue: true,
            },
            {
                prop: "showRosterNotes",
                type: "boolean",
                label: "Show Notes",
                defaultValue: true,
            },
            {
                prop: "showIsMember",
                type: "boolean",
                label: "Show Member Column",
                defaultValue: true,
            },
            {
                prop: "showIsBoard",
                type: "boolean",
                label: "Show Board Column",
                defaultValue: true,
            },
            {
                prop: "showIsPoc",
                type: "boolean",
                label: "Show Primary POC Column",
                defaultValue: true,
            },
            {
                prop: "showIsEditor",
                type: "boolean",
                label: "Show Editor Column",
                defaultValue: true,
            },
            {
                prop: "showSegmentColumns",
                type: "boolean",
                label: "Show Segment Columns",
                defaultValue: true,
            },
            {
                prop: "rowFilter",
                type: "rosterRowFilter",
                label: "Row Filter",
                defaultValue: "all",
            },
            {
                prop: "viewMode",
                type: "select",
                label: "View Mode",
                params: {
                    options: [
                        { value: "table", label: "Table" },
                        { value: "accordion", label: "Collapsible" },
                    ],
                },
                defaultValue: "accordion",
            },
            {
                prop: "accordionStartOpen",
                type: "select",
                label: "Accordion Start State",
                params: {
                    options: [
                        { value: "true", label: "Open" },
                        { value: "false", label: "Closed" },
                    ],
                },
                defaultValue: "false",
            },
        ],
        editing: ({ values, editingInfo }: any) => {
            return applyRosterAccordionEditing(values, editingInfo);
        },
    },
    {
        id: "DocumentsAccordion",
        label: "Club Documents (Auto)",
        thumbnail: "https://friscolakes.net/easyblocks-web-builder/build/thumbnails/documents-accordion.svg",
        schema: [
            {
                prop: "bgClass",
                type: "backgroundColor",
                label: "Background Color",
                defaultValue: "text-bg-white",
            },
            {
                prop: "paddingYClass",
                type: "select",
                label: "Padding (Vertical)",
                params: {
                    options: [
                        { value: "py-0", label: "None" },
                        { value: "py-section-sm", label: "Small" },
                        { value: "py-section-md", label: "Medium" },
                        { value: "py-section-lg", label: "Large" },
                    ],
                },
                defaultValue: "py-section-md",
            },
            {
                prop: "fixedCategoryId",
                type: "documentCategory",
                label: "Documents Category",
                defaultValue: 0,
            },
            {
                prop: "categoryTitleOverride",
                type: "string",
                label: "Category Title Override",
                defaultValue: "",
            },
            {
                prop: "fixedYear",
                type: "documentYear",
                label: "Documents Year",
                defaultValue: 0,
            },
            {
                prop: "viewMode",
                type: "select",
                label: "View Mode",
                params: {
                    options: [
                        { value: "table", label: "Table" },
                        { value: "accordion", label: "Collapsible" },
                    ],
                },
                defaultValue: "accordion",
            },
            {
                prop: "accordionStartOpen",
                type: "select",
                label: "Accordion Start State",
                params: {
                    options: [
                        { value: "true", label: "Open" },
                        { value: "false", label: "Closed" },
                    ],
                },
                defaultValue: "false",
            },
        ],
        editing: ({ values, editingInfo }: any) => {
            const isAccordion = values?.viewMode === "accordion";
            const fields = (editingInfo?.fields ?? []).map((field: any) => {
                if (field?.path === "fixedYear") {
                    const parsedFixedCategoryId = Number(values?.fixedCategoryId);
                    const fixedCategoryId =
                        Number.isFinite(parsedFixedCategoryId) && parsedFixedCategoryId > 0 ? parsedFixedCategoryId : 0;
                    return {
                        ...field,
                        params: {
                            ...(field?.params ?? {}),
                            fixedCategoryId,
                        },
                    };
                }
                // Hide accordion start state option when table mode is selected
                if (!isAccordion && field?.path === "accordionStartOpen") {
                    return { ...field, visible: false };
                }
                return field;
            });
            return {
                ...(editingInfo ?? {}),
                fields,
            };
        },
    },
    {
        id: "ResidentDirectorySection",
        label: "Resident Directory (Auto)",
        thumbnail: "https://friscolakes.net/easyblocks-web-builder/build/thumbnails/neighborhoods-accordion.svg",
        schema: [
            {
                prop: "bgClass",
                type: "backgroundColor",
                label: "Background Color",
                defaultValue: "text-bg-white",
            },
            {
                prop: "paddingYClass",
                type: "select",
                label: "Padding (Vertical)",
                params: {
                    options: [
                        { value: "py-0", label: "None" },
                        { value: "py-section-sm", label: "Small" },
                        { value: "py-section-md", label: "Medium" },
                        { value: "py-section-lg", label: "Large" },
                    ],
                },
                defaultValue: "py-section-md",
            },
            {
                prop: "title",
                type: "string",
                label: "Section Title",
                defaultValue: "Resident Directory",
            },
        ],
    },
    {
        id: "ListTemplateSection",
        label: "List Template HTML (Auto)",
        schema: [
            {
                prop: "listTemplateName",
                type: "string",
                label: "List Template Name",
                defaultValue: "",
            },
        ],
    },
    {
        id: "ButtonLink",
        label: "Button / Link",
        thumbnail: "https://friscolakes.net/easyblocks-web-builder/build/thumbnails/button-link.svg",
        schema: [
            { prop: "label", type: "string", label: "Link Text", defaultValue: "Click Me" },
            { prop: "href", type: "string", label: "Link" },
            {
                prop: "newWindow",
                type: "boolean",
                label: "Open in New Window",
                defaultValue: false,
            },
            {
                prop: "isLink",
                type: "select",
                label: "Style",
                params: {
                    options: [
                        { value: "false", label: "Button" },
                        { value: "true", label: "Text Link" },
                    ],
                },
                defaultValue: "false",
            },
            {
                prop: "width",
                type: "select",
                label: "Width",
                params: {
                    options: [
                        { value: "auto", label: "Auto Width" },
                        { value: "full", label: "Full Width" },
                    ],
                },
                defaultValue: "auto",
            },
            {
                prop: "horizontalAlign",
                type: "select",
                label: "Component Horizontal Alignment",
                params: { options: textAlignClassOptions },
                defaultValue: "no-class",
            },
            {
                prop: "paddingClass",
                type: "select",
                label: "Padding (Vertical)",
                params: {
                    options: [
                        { value: "py-0", label: "None" },
                        { value: "py-section-sm", label: "Small" },
                        { value: "py-section-md", label: "Medium" },
                        { value: "py-section-lg", label: "Large" },
                    ],
                },
                defaultValue: "py-0",
            },
            {
                prop: "variant",
                type: "buttonColor",
                label: "Button Color",
                defaultValue: "primary",
            },
            {
                prop: "outline",
                type: "boolean",
                label: "Outline",
                defaultValue: false,
            },
            {
                prop: "size",
                type: "select",
                label: "Size",
                params: {
                    options: [
                        { value: "sm", label: "Small" },
                        { value: "md", label: "Medium" },
                        { value: "lg", label: "Large" },
                    ],
                },
                defaultValue: "md",
            },
        ],
        editing: ({ values, editingInfo }: any) => {
            const isLink = values?.isLink === "true";
            const fields = (editingInfo?.fields ?? []).map((field: any) => {
                // Hide variant, outline, size, width, horizontalAlign, and paddingClass when Text Link is selected
                if (isLink && (field?.path === "variant" || field?.path === "outline" || field?.path === "size" || field?.path === "width" || field?.path === "horizontalAlign" || field?.path === "paddingClass")) {
                    return { ...field, visible: false };
                }
                return field;
            });
            return {
                ...(editingInfo ?? {}),
                fields,
            };
        },
    },
    {
        id: "DocumentLink",
        label: "Single Document Link",
        thumbnail: "https://friscolakes.net/easyblocks-web-builder/build/thumbnails/document-link.svg",
        schema: [
            { prop: "document", type: "document", label: "Document", isLabelHidden: true },
            {
                prop: "horizontalAlignClass",
                type: "select",
                label: "Component Horizontal Alignment",
                params: { options: blockAlignClassOptions },
                defaultValue: "no-class",
            },
            {
                prop: "paddingClass",
                type: "select",
                label: "Padding (Vertical)",
                params: {
                    options: [
                        { value: "py-0", label: "None" },
                        { value: "py-section-sm", label: "Small" },
                        { value: "py-section-md", label: "Medium" },
                        { value: "py-section-lg", label: "Large" },
                    ],
                },
                defaultValue: "py-0",
            },
        ],
    },

    {
        id: "ImageBlock",
        label: "Image",
        thumbnail: "https://friscolakes.net/easyblocks-web-builder/build/thumbnails/image-block.svg",
        schema: [
            { prop: "src", type: "media", label: "Image", isLabelHidden: true, defaultValue: "https://placehold.co/400x400" },
            { prop: "alt", type: "string", label: "Alt Text", defaultValue: "Image description" },
            {
                prop: "horizontalAlignClass",
                type: "select",
                label: "Component Horizontal Alignment",
                params: { options: blockAlignClassOptions },
                defaultValue: "no-class",
            },
            {
                prop: "paddingClass",
                type: "select",
                label: "Padding (Vertical)",
                params: {
                    options: [
                        { value: "py-0", label: "None" },
                        { value: "py-section-sm", label: "Small" },
                        { value: "py-section-md", label: "Medium" },
                        { value: "py-section-lg", label: "Large" },
                    ],
                },
                defaultValue: "py-0",
            },
            {
                prop: "border",
                type: "boolean",
                label: "Borders Enabled",
                defaultValue: false,
            },
            {
                prop: "borderWidth",
                type: "select",
                label: "Border Width",
                params: {
                    options: [
                        { value: "1", label: "1" },
                        { value: "2", label: "2" },
                        { value: "3", label: "3" },
                        { value: "4", label: "4" },
                        { value: "5", label: "5" },
                    ],
                },
                defaultValue: "1",
            },
            {
                prop: "borderColorClass",
                type: "borderColor",
                label: "Border Color",
                defaultValue: "no-class",
            },
            {
                prop: "borderRadius",
                type: "select",
                label: "Border Radius",
                params: {
                    options: [
                        { value: "none", label: "None" },
                        { value: "sm", label: "Small" },
                        { value: "md", label: "Medium" },
                        { value: "lg", label: "Large" },
                        { value: "circle", label: "Circle" },
                    ],
                },
                defaultValue: "none",
            },
        ],
        editing: ({ editingInfo, params }: any) => {
            if (!params?.[CARD_IMAGE_CONTEXT_PARAM]) {
                return editingInfo;
            }

            return hideEditingFields(editingInfo, [
                "horizontalAlignClass",
                "paddingClass",
                "border",
                "borderWidth",
                "borderColorClass",
                "borderRadius",
            ]);
        },
    },

    {
        id: "HeroImageSection",
        label: "Hero Image Section — Full-width image banner without text overlay",
        thumbnail: "https://friscolakes.net/easyblocks-web-builder/build/thumbnails/hero-image-section.svg",
        schema: [
            {
                prop: "bgClass",
                type: "backgroundColor",
                label: "Background Color",
                defaultValue: "text-bg-white",
            },
            {
                prop: "paddingYClass",
                type: "select",
                label: "Padding (Vertical)",
                params: {
                    options: [
                        { value: "py-0", label: "None" },
                        { value: "py-section-sm", label: "Small" },
                        { value: "py-section-md", label: "Medium" },
                        { value: "py-section-lg", label: "Large" },
                    ],
                },
                defaultValue: "py-section-md",
            },
            {
                prop: "imageWidth",
                type: "select",
                label: "Image Width",
                params: {
                    options: [
                        { value: "content", label: "Content Area Max-Width" },
                        { value: "full", label: "Full Viewport Width" },
                    ],
                },
                defaultValue: "content",
            },
            {
                prop: "image",
                type: "media",
                label: "Image",
                isLabelHidden: true,
                defaultValue: "",
            },
            {
                prop: "imageAlt",
                type: "string",
                label: "Image Alt Text",
                defaultValue: "",
            },
        ],
    },

    {
        id: "Card",
        label: "Card",
        thumbnail: "https://friscolakes.net/easyblocks-web-builder/build/thumbnails/news-card.svg",
        schema: [
            {
                prop: "Image",
                type: "component",
                label: "Image",
                accepts: ["ImageBlock"],
                placeholderAppearance: { label: "Image", width: 180, height: 48 },
            },
            {
                prop: "Headline",
                type: "component",
                label: "Headline",
                accepts: ["HeadingBlock"],
                placeholderAppearance: { label: "Headline", width: 180, height: 48 },
            },
            {
                prop: "RichText",
                type: "component",
                label: "Rich Text",
                accepts: ["QuillText"],
                placeholderAppearance: { label: "Rich Text", width: 180, height: 48 },
            },
            {
                prop: "Button",
                type: "component",
                label: "Button",
                accepts: ["ButtonLink"],
                placeholderAppearance: { label: "Button", width: 180, height: 48 },
            },
            {
                prop: "cardBgClass",
                type: "backgroundColor",
                label: "Card Background",
                defaultValue: "text-bg-white",
            },
            {
                prop: "cardBorderClass",
                type: "select",
                label: "Card Border Width",
                params: { options: borderWidthClassOptions },
                defaultValue: "no-class",
            },
            {
                prop: "cardBorderColorClass",
                type: "borderColor",
                label: "Card Border Color",
                defaultValue: "no-class",
            },
            {
                prop: "contentAlignClass",
                type: "select",
                label: "Content Horizontal Alignment",
                params: { options: textAlignClassOptions },
                defaultValue: "text-center",
            },
            {
                prop: "buttonColorClass",
                type: "buttonColor",
                label: "Button Color",
                defaultValue: "primary",
            },
        ],
        styles: () => ({
            components: {
                Image: {
                    [CARD_IMAGE_CONTEXT_PARAM]: true,
                },
            },
        }),
    },

    {
        id: "SingleColumn",
        label: "Single Column — Centered content area for text, images, and buttons",
        thumbnail: "https://friscolakes.net/easyblocks-web-builder/build/thumbnails/single-column.svg",
        schema: [
            {
                prop: "bgClass",
                type: "backgroundColor",
                label: "Background Color",
                defaultValue: "text-bg-white",
            },
            {
                prop: "paddingYClass",
                type: "select",
                label: "Padding (Vertical)",
                params: {
                    options: [
                        { value: "py-0", label: "None" },
                        { value: "py-section-sm", label: "Small" },
                        { value: "py-section-md", label: "Medium" },
                        { value: "py-section-lg", label: "Large" },
                    ],
                },
                defaultValue: "py-section-md",
            },
            {
                prop: "columnWidthClass",
                type: "select",
                label: "Column Width",
                params: {
                    options: [
                        { value: "no-class", label: "Wide" },
                        { value: "container-max-xl", label: "Medium" },
                        { value: "container-max-md", label: "Narrow" },
                    ],
                },
                defaultValue: "no-class",
            },
            {
                prop: "contentAlignClass",
                type: "select",
                label: "Section Horizontal Alignment",
                params: { options: sectionContentAlignClassOptions },
                defaultValue: "align-items-start text-start",
            },
            {
                prop: "Content",
                type: "component-collection",
                accepts: [...sectionBodyContentAccepts],
                placeholderAppearance: { label: "Content", height: 48 },
            },
        ],
    },

    {
        id: "CardsSection",
        label: "Cards Section — Row of cards with image, headline, text, and button",
        thumbnail: "https://friscolakes.net/easyblocks-web-builder/build/thumbnails/news-latest-row.svg",
        schema: [
            {
                prop: "bgClass",
                type: "backgroundColor",
                label: "Background Color",
                defaultValue: "text-bg-white",
            },
            {
                prop: "paddingYClass",
                type: "select",
                label: "Padding (Vertical)",
                params: {
                    options: [
                        { value: "py-0", label: "None" },
                        { value: "py-section-sm", label: "Small" },
                        { value: "py-section-md", label: "Medium" },
                        { value: "py-section-lg", label: "Large" },
                    ],
                },
                defaultValue: "py-section-md",
            },
            {
                prop: "cardCount",
                type: "select",
                label: "Number of Cards",
                params: {
                    options: [
                        { value: "3", label: "3" },
                        { value: "4", label: "4" },
                        { value: "6", label: "6" },
                        { value: "8", label: "8" },
                    ],
                },
                defaultValue: "3",
            },
            {
                prop: "cardBgClass",
                type: "backgroundColor",
                label: "Card Background",
                defaultValue: "text-bg-white",
            },
            {
                prop: "cardBorderClass",
                type: "select",
                label: "Card Border Width",
                params: { options: borderWidthClassOptions },
                defaultValue: "no-class",
            },
            {
                prop: "cardBorderColorClass",
                type: "borderColor",
                label: "Card Border Color",
                defaultValue: "no-class",
            },
            {
                prop: "contentAlignClass",
                type: "select",
                label: "Content Horizontal Alignment",
                params: { options: textAlignClassOptions },
                defaultValue: "no-class",
            },
            {
                prop: "buttonColorClass",
                type: "buttonColor",
                label: "Button Color",
                defaultValue: "primary",
            },
            ...Array.from({ length: 8 }, (_, idx) => ({
                prop: `Card${idx + 1}`,
                type: "component" as const,
                label: `Card ${idx + 1}`,
                accepts: ["Card"],
                placeholderAppearance: { label: `Card ${idx + 1}`, height: 48 },
            })),
        ],
        editing: ({ values, editingInfo }: any) => {
            const parsedCardCount = Number(values?.cardCount);
            const maxCards = [3, 4, 6, 8].includes(parsedCardCount) ? parsedCardCount : 3;
            const fields = (editingInfo?.fields ?? []).map((field: any) => {
                const match = /^Card([1-8])$/.exec(field?.path ?? "");
                if (match && Number(match[1]) > maxCards) {
                    return { ...field, visible: false };
                }
                return field;
            });
            return {
                ...(editingInfo ?? {}),
                fields,
            };
        },
    },

    {
        id: "GridSection",
        label: "Grid Section — Flexible grid layout for cards and content blocks",
        thumbnail: "https://friscolakes.net/easyblocks-web-builder/build/thumbnails/grid-section.svg",
        schema: [
            {
                prop: "bgClass",
                type: "backgroundColor",
                label: "Background Color",
                defaultValue: "text-bg-white",
            },
            {
                prop: "paddingYClass",
                type: "select",
                label: "Padding (Vertical)",
                params: {
                    options: [
                        { value: "py-0", label: "None" },
                        { value: "py-section-sm", label: "Small" },
                        { value: "py-section-md", label: "Medium" },
                        { value: "py-section-lg", label: "Large" },
                    ],
                },
                defaultValue: "py-section-md",
            },
            {
                prop: "columns",
                type: "select",
                label: "Columns",
                params: {
                    options: [
                        { value: "2", label: "2" },
                        { value: "3", label: "3" },
                        { value: "4", label: "4" },
                    ],
                },
                defaultValue: "2",
            },
            {
                prop: "rows",
                type: "select",
                label: "Rows",
                params: {
                    options: [
                        { value: "2", label: "2" },
                        { value: "3", label: "3" },
                        { value: "4", label: "4" },
                    ],
                },
                defaultValue: "2",
            },
            {
                prop: "contentAlignClass",
                type: "select",
                label: "Section Horizontal Alignment",
                params: { options: sectionContentAlignClassOptions },
                defaultValue: "align-items-start text-start",
            },
            {
                prop: "verticalAlignClass",
                type: "select",
                label: "Section Vertical Alignment",
                params: {
                    options: [
                        { value: "justify-content-start", label: "Top" },
                        { value: "justify-content-center", label: "Center" },
                        { value: "justify-content-end", label: "Bottom" },
                    ],
                },
                defaultValue: "justify-content-start",
            },
            // 4x4 = 16 cells. We render only the first (rows * columns).
            ...Array.from({ length: 16 }, (_, idx) => {
                const n = idx + 1;
                return {
                    prop: `Cell${n}`,
                    type: "component-collection" as const,
                    accepts: [...multiColumnAndGridContentAccepts],
                    placeholderAppearance: { label: `Cell ${n} Content`, height: 48 },
                };
            }),
        ],
    },

    {
        id: "CTASection",
        label: "Call to Action Section — Highlighted section with title, text, and button",
        thumbnail: "https://friscolakes.net/easyblocks-web-builder/build/thumbnails/cta-section.svg",
        schema: [
            {
                prop: "bgClass",
                type: "backgroundColor",
                label: "Background Color",
                defaultValue: "text-bg-primary-700",
            },
            {
                prop: "paddingYClass",
                type: "select",
                label: "Padding (Vertical)",
                params: {
                    options: [
                        { value: "py-0", label: "None" },
                        { value: "py-section-sm", label: "Small" },
                        { value: "py-section-md", label: "Medium" },
                        { value: "py-section-lg", label: "Large" },
                    ],
                },
                defaultValue: "py-section-md",
            },
            {
                prop: "contentAlignClass",
                type: "select",
                label: "Section Horizontal Alignment",
                params: { options: sectionContentAlignClassOptions },
                defaultValue: "align-items-center text-center",
            },
            {
                prop: "innerBgClass",
                type: "backgroundColor",
                label: "Inner Background Color",
                defaultValue: "bg-transparent",
            },
            {
                prop: "innerPaddingYClass",
                type: "select",
                label: "Inner Padding",
                params: {
                    options: [
                        { value: "py-0 px-0", label: "None" },
                        { value: "py-section-sm px-3", label: "Small" },
                        { value: "py-section-md px-3", label: "Medium" },
                        { value: "py-section-lg px-5", label: "Large" },
                    ],
                },
                defaultValue: "py-0 px-0",
            },
            {
                prop: "Title",
                type: "component",
                label: "Title",
                accepts: ["HeadingBlock"],
                placeholderAppearance: { label: "Title", width: 240, height: 48 },
            },
            {
                prop: "SubtitleOrText",
                type: "component",
                label: "Subtitle or Text",
                accepts: ["HeadingBlock", "QuillText"],
                placeholderAppearance: { label: "Subtitle or Text", width: 240, height: 48 },
            },
            {
                prop: "CTAButton",
                type: "component",
                label: "CTA Button",
                accepts: ["ButtonLink"],
                placeholderAppearance: { label: "CTA Button", width: 240, height: 48 },
            },
        ],
    },

    {
        id: "ColumnsSection",
        label: "Multi-Column Section — Side-by-side columns for text and images",
        thumbnail: "https://friscolakes.net/easyblocks-web-builder/build/thumbnails/columns-section.svg",
        schema: [
            {
                prop: "bgClass",
                type: "backgroundColor",
                label: "Background Color",
                defaultValue: "text-bg-white",
            },
            {
                prop: "paddingYClass",
                type: "select",
                label: "Padding (Vertical)",
                params: {
                    options: [
                        { value: "py-0", label: "None" },
                        { value: "py-section-sm", label: "Small" },
                        { value: "py-section-md", label: "Medium" },
                        { value: "py-section-lg", label: "Large" },
                    ],
                },
                defaultValue: "py-section-md",
            },
            {
                prop: "columns",
                type: "select",
                label: "Columns",
                params: {
                    options: [
                        { value: "2", label: "2" },
                        { value: "3", label: "3" },
                        { value: "4", label: "4" },
                    ],
                },
                defaultValue: "2",
            },
            {
                prop: "contentAlignClass",
                type: "select",
                label: "Section Horizontal Alignment",
                params: { options: sectionContentAlignClassOptions },
                defaultValue: "align-items-start text-start",
            },
            {
                prop: "verticalAlignClass",
                type: "select",
                label: "Section Vertical Alignment",
                params: {
                    options: [
                        { value: "justify-content-start", label: "Top" },
                        { value: "justify-content-center", label: "Center" },
                        { value: "justify-content-end", label: "Bottom" },
                    ],
                },
                defaultValue: "justify-content-start",
            },
            {
                prop: "Column1",
                type: "component-collection",
                accepts: [...multiColumnAndGridContentAccepts],
                placeholderAppearance: { label: "Column 1 Content", height: 48 },
            },
            {
                prop: "Column2",
                type: "component-collection",
                accepts: [...multiColumnAndGridContentAccepts],
                placeholderAppearance: { label: "Column 2 Content", height: 48 },
            },
            {
                prop: "Column3",
                type: "component-collection",
                accepts: [...multiColumnAndGridContentAccepts],
                placeholderAppearance: { label: "Column 3 Content", height: 48 },
            },
            {
                prop: "Column4",
                type: "component-collection",
                accepts: [...multiColumnAndGridContentAccepts],
                placeholderAppearance: { label: "Column 4 Content", height: 48 },
            },
        ],
    },

    {
        id: "PhotoAlbum",
        label: "Photo Albums (Auto)",
        thumbnail: "https://friscolakes.net/easyblocks-web-builder/build/thumbnails/carousel.svg",
        schema: [
            {
                prop: "bgClass",
                type: "backgroundColor",
                label: "Background Color",
                defaultValue: "text-bg-white",
            },
            {
                prop: "paddingYClass",
                type: "select",
                label: "Padding (Vertical)",
                params: {
                    options: [
                        { value: "py-0", label: "None" },
                        { value: "py-section-sm", label: "Small" },
                        { value: "py-section-md", label: "Medium" },
                        { value: "py-section-lg", label: "Large" },
                    ],
                },
                defaultValue: "py-section-md",
            },
            {
                prop: "slideshowWidthClass",
                type: "select",
                label: "Slideshow Width",
                params: {
                    options: [
                        { value: "container-max-md", label: "Narrow" },
                        { value: "container-max-xl", label: "Medium" },
                        { value: "no-class", label: "Wide" },
                    ],
                },
                defaultValue: "container-max-xl",
            },
            {
                prop: "fixedAlbumId",
                type: "album",
                label: "Album",
                defaultValue: 0,
            },
            {
                prop: "viewMode",
                type: "select",
                label: "View Mode",
                params: {
                    options: [
                        { value: "slideshow", label: "Slideshow" },
                        { value: "accordion", label: "Slideshow in Collapsible" },
                    ],
                },
                defaultValue: "slideshow",
            },
            {
                prop: "accordionStartOpen",
                type: "select",
                label: "Accordion Start State",
                params: {
                    options: [
                        { value: "true", label: "Open" },
                        { value: "false", label: "Closed" },
                    ],
                },
                defaultValue: "false",
            },
        ],
        editing: ({ values, editingInfo }: any) => {
            const isAccordion = values?.viewMode === "accordion";
            const fields = (editingInfo?.fields ?? []).map((field: any) => {
                // Hide accordion start state option when slideshow mode is selected
                if (!isAccordion && field?.path === "accordionStartOpen") {
                    return { ...field, visible: false };
                }
                return field;
            });
            return {
                ...(editingInfo ?? {}),
                fields,
            };
        },
    },
    {
        id: "AccordionSection",
        label: "Collapsible Content — Expandable FAQ or content panels",
        thumbnail: "https://friscolakes.net/easyblocks-web-builder/build/thumbnails/accordion-section.svg",
        schema: [
            {
                prop: "bgClass",
                type: "backgroundColor",
                label: "Background Color",
                defaultValue: "text-bg-white",
            },
            {
                prop: "paddingYClass",
                type: "select",
                label: "Padding (Vertical)",
                params: {
                    options: [
                        { value: "py-0", label: "None" },
                        { value: "py-section-sm", label: "Small" },
                        { value: "py-section-md", label: "Medium" },
                        { value: "py-section-lg", label: "Large" },
                    ],
                },
                defaultValue: "py-section-md",
            },
            {
                prop: "Items",
                type: "component-collection",
                accepts: ["AccordionItem"],
                placeholderAppearance: { label: "Accordion Item", height: 48 },
            },
        ],
    },

    {
        id: "VideoEmbedSection",
        label: "Video Embed — YouTube or Vimeo video player",
        thumbnail: "https://friscolakes.net/easyblocks-web-builder/build/thumbnails/video-embed-section.svg",
        schema: [
            {
                prop: "bgClass",
                type: "backgroundColor",
                label: "Background Color",
                defaultValue: "text-bg-white",
            },
            {
                prop: "paddingYClass",
                type: "select",
                label: "Padding (Vertical)",
                params: {
                    options: [
                        { value: "py-0", label: "None" },
                        { value: "py-section-sm", label: "Small" },
                        { value: "py-section-md", label: "Medium" },
                        { value: "py-section-lg", label: "Large" },
                    ],
                },
                defaultValue: "py-section-md",
            },
            {
                prop: "videoUrl",
                type: "string",
                label: "Video URL",
                defaultValue: "",
            },
            {
                prop: "videoWidthClass",
                type: "select",
                label: "Video Width",
                params: {
                    options: [
                        { value: "container-max-md", label: "Narrow" },
                        { value: "container-max-xl", label: "Medium" },
                        { value: "no-class", label: "Wide" },
                    ],
                },
                defaultValue: "container-max-xl",
            },
            {
                prop: "viewMode",
                type: "select",
                label: "View Mode",
                params: {
                    options: [
                        { value: "video", label: "Video" },
                        { value: "accordion", label: "Video in Collapsible" },
                    ],
                },
                defaultValue: "video",
            },
            {
                prop: "accordionStartOpen",
                type: "select",
                label: "Accordion Start State",
                params: {
                    options: [
                        { value: "true", label: "Open" },
                        { value: "false", label: "Closed" },
                    ],
                },
                defaultValue: "false",
            },
        ],
        editing: ({ values, editingInfo }: any) => {
            const isAccordion = values?.viewMode === "accordion";
            const fields = (editingInfo?.fields ?? []).map((field: any) => {
                if (!isAccordion && field?.path === "accordionStartOpen") {
                    return { ...field, visible: false };
                }
                return field;
            });
            return {
                ...(editingInfo ?? {}),
                fields,
            };
        },
    },

    {
        id: "AccordionItem",
        label: "Accordion Item",
        thumbnail: "https://friscolakes.net/easyblocks-web-builder/build/thumbnails/accordion-item.svg",
        schema: [
            {
                prop: "heading",
                type: "string",
                label: "Heading",
                defaultValue: "Heading",
            },
            {
                prop: "contentAlignClass",
                type: "select",
                label: "Body Horizontal Alignment",
                params: { options: sectionContentAlignClassOptions },
                defaultValue: "align-items-start text-start",
            },
            {
                prop: "Answer",
                type: "component-collection",
                label: "Content",
                accepts: [...sectionBodyContentAccepts],
                placeholderAppearance: { label: "Body Content", height: 48 },
            },
        ],
    },
];
